// Auto-load sentinel for infinite-scroll lists. Drops a zero-height
// div at the bottom of a paginated list; when the user scrolls it into
// view we call `onVisible()` so the host component can fetch the next
// page. Pairs naturally with `usePaginatedList`.
//
// `onVisible` should be safe to call when already loading — the
// hook-level `loadMore()` short-circuits on `isFetchingNextPage`.
//
// The `rootMargin` default of 200px means "trigger when the user is
// within 200px of the bottom", which gives the network round-trip a
// head start so the new items are usually already mounted by the time
// the user actually reaches the previous footer.
// (DEV-16164.)
import { useEffect, useRef } from 'react';

export function InfiniteScrollSentinel({
  onVisible,
  enabled = true,
  rootMargin = '200px',
}: {
  onVisible: () => void;
  /** False when there's nothing more to load — skip wiring the observer. */
  enabled?: boolean;
  /** Forwarded to IntersectionObserver. Default trips 200px before bottom. */
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Hold the latest callback in a ref so the observer effect can stay
  // mounted across re-renders without tearing down on every render of
  // the host (which would happen if `onVisible` were in the dep list).
  const cbRef = useRef(onVisible);
  useEffect(() => {
    cbRef.current = onVisible;
  }, [onVisible]);

  useEffect(() => {
    if (!enabled) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            cbRef.current();
            break;
          }
        }
      },
      { rootMargin },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [enabled, rootMargin]);

  return <div ref={ref} style={{ height: 1, width: '100%' }} aria-hidden="true" />;
}
